import { useSearchParams } from "react-router-dom";
import { TrackingHeader } from "../components/tracking/trackingHeader";
import { TrackingSummary } from "../components/tracking/trackingSummary";
import { TrackingDetail } from "../components/tracking/trackingDetail";
import { TrackingShippingOverview } from "../components/tracking/trackingShippingOverview";
import { TrackingServices } from "../components/tracking/trackingServices";
import { TrackingPackageDetails } from "../components/tracking/trackingPackageDetails";
import { useGetFedexTracking } from "../api/fedex.api";
import StyledSpinner from "../components/styled/StyledSpinner";

const ErrorMessage = ({ locale }: { locale: string }) => {
  if (locale === "fr") {
    return (
      <div className="flex w-[100vw] flex-1 flex-col items-center justify-center gap-2">
        <p>Erreur lors du chargement des informations</p>
        <p>Veuillez réessayer plus tard</p>
      </div>
    );
  }
  return (
    <div className="flex w-[100vw] flex-1 flex-col items-center justify-center gap-2">
      <p>Error loading tracking information</p>
      <p>Please try again later</p>
    </div>
  );
};

const LoadingMessage = ({ locale }: { locale: string }) => {
  if (locale === "fr") {
    return (
      <div className="flex w-[100vw] flex-1 items-center justify-center gap-4">
        <span>Chargement des informations...</span>
        <StyledSpinner />
      </div>
    );
  }
  return (
    <div className="flex w-[100vw] flex-1 items-center justify-center gap-4">
      <p>Loading tracking information...</p>
      <StyledSpinner />
    </div>
  );
};

export const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const trackingCode = searchParams.get("code") || "";
  const locale = searchParams.get("locale") || "fr";
  const { data, isLoading, isError, isSuccess } = useGetFedexTracking(trackingCode, locale);

  if (isLoading) return <LoadingMessage locale={locale} />;

  if (
    isError ||
    !isSuccess ||
    !data ||
    !data.fedexResponse ||
    !data.fedexResponse.output ||
    !data.fedexResponse.output.completeTrackResults
  ) {
    return <ErrorMessage locale={locale} />;
  }

  const hasCompleteTrackResults =
    Array.isArray(data?.fedexResponse.output.completeTrackResults) &&
    data?.fedexResponse.output.completeTrackResults.length > 0;
  const hasTrackResults =
    hasCompleteTrackResults &&
    Array.isArray(data?.fedexResponse.output.completeTrackResults[0].trackResults) &&
    data?.fedexResponse.output.completeTrackResults[0].trackResults.length > 0;
  const trackResults = hasTrackResults
    ? data?.fedexResponse.output.completeTrackResults[0].trackResults[0]
    : null;

  if (!trackResults) {
    return <ErrorMessage locale={locale} />;
  }

  const scanEvents = trackResults.scanEvents;
  const dateAndTimes = trackResults.dateAndTimes || [];
  const lastScanEvent = trackResults.latestStatusDetail;
  const standardTransitTimeWindow = trackResults.standardTransitTimeWindow;
  const specialHandlings = trackResults.specialHandlings || [];
  const serviceDetail = trackResults.serviceDetail;
  const packageDetails = trackResults.packageDetails;
  const shipmentDetails = trackResults.shipmentDetails;
  const expedition = data?.expedition;

  if (
    !expedition ||
    !scanEvents ||
    !lastScanEvent ||
    !standardTransitTimeWindow ||
    !specialHandlings ||
    !serviceDetail ||
    !packageDetails
  ) {
    if (locale === "fr") {
      return <div>Aucun suivi disponible</div>;
    }
    return <div>No tracking available</div>;
  }

  return (
    <div className="flex min-h-screen w-screen flex-col items-center justify-start gap-8 bg-gray-50 p-4 md:px-10 lg:px-20 lg:py-12">
      <TrackingHeader code={expedition.identifier} status={expedition.deliveryStatus} />
      {scanEvents && scanEvents.length > 0 ? (
        <div className="flex w-full flex-col gap-8">
          <TrackingSummary scanEvents={scanEvents} lastScanEvent={lastScanEvent} locale={locale} />
          <div className="flex w-full flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
            <TrackingDetail scanEvents={scanEvents} locale={locale} />
            <div className="flex h-full w-full flex-col items-start gap-8 lg:w-1/3">
              <TrackingShippingOverview
                dateAndTimes={dateAndTimes}
                trackingNumber={expedition.trackingCode}
                standardTransitTimeWindow={standardTransitTimeWindow}
                locale={locale}
              />
              <TrackingServices
                specialHandlings={specialHandlings}
                serviceDetail={serviceDetail}
                locale={locale}
              />
              <TrackingPackageDetails
                packageDetails={packageDetails}
                shipmentDetails={shipmentDetails}
                locale={locale}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>{locale === "fr" ? "Aucun suivi disponible" : "No tracking available"}</div>
      )}
    </div>
  );
};
